import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '@spark';

import { useAuth } from '@/features/auth/AuthContext.jsx';

import { fetchPlatformMe } from '../api/waitlistApi';
import { waitlistDraftKey } from '../utils/waitlistDraft';
import WaitlistDone from './WaitlistDone';
import WaitlistLayout from './WaitlistLayout';
import WaitlistWizard from './WaitlistWizard';

const ROLES_WITH_ACCESS = new Set(['god', 'creator']);

const isCompletedPendingRequest = (request) =>
    Boolean(request && request.status === 'pending' && request.questionnaire_completed_at);

/**
 * Authenticated waiting-list entry at `/waitlist/apply`.
 *
 * `POST /users/bootstrap` sends strangers here (state `no_access`).  The page
 * reads `GET /platform/me` and shows the questionnaire while the pending
 * request has no answers yet, or the "We Got Your Request!" confirmation once
 * it does — so a returning applicant never re-triggers the email.
 *
 * None of these steps carries a "Log out" in the top-right slot: the
 * applicant has only just signed in with Google and has no account on the
 * platform yet, so the chrome stays bare, and the confirmation's single
 * "Return to Specific Website" button signs out and lands on `/login`.  The
 * wizard's step and answers are drafted per applicant email
 * (`utils/waitlistDraft.js`) so a refresh resumes where they were.
 */
export default function WaitlistApplyPage() {
    const navigate = useNavigate();
    const { user, loading, isAuthEnabled, logout } = useAuth();
    const [me, setMe] = useState(undefined);
    const [completedRequest, setCompletedRequest] = useState(null);

    useEffect(() => {
        if (loading) return undefined;
        if (isAuthEnabled && !user) {
            navigate('/login', { replace: true });
            return undefined;
        }
        let cancelled = false;
        fetchPlatformMe().then((data) => {
            if (!cancelled) setMe(data);
        });
        return () => {
            cancelled = true;
        };
    }, [loading, user, isAuthEnabled, navigate]);

    useEffect(() => {
        if (me && ROLES_WITH_ACCESS.has(me.platform_role)) {
            navigate('/', { replace: true });
        }
    }, [me, navigate]);

    const signOut = useCallback(async () => {
        try {
            await logout();
        } catch (err) {
            // The session cookie may survive, but the login page re-runs bootstrap and parks them here again.
            console.warn('Logout failed', err);
        } finally {
            navigate('/login', { replace: true });
        }
    }, [logout, navigate]);

    if (loading || me === undefined) {
        return (
            <WaitlistLayout variant="wizard">
                <div className="waitlist-loading">
                    <Spinner size="large" aria-label="Loading your request" />
                </div>
            </WaitlistLayout>
        );
    }

    const request = completedRequest || me?.creator_request || null;
    if (completedRequest || isCompletedPendingRequest(request)) {
        // No "Log out" in the corner here: "Return to Specific Website" already signs the applicant out.
        return (
            <WaitlistLayout variant="done">
                <WaitlistDone onReturn={signOut} />
            </WaitlistLayout>
        );
    }

    return (
        <WaitlistLayout variant="wizard">
            <WaitlistWizard
                draftKey={waitlistDraftKey(user)}
                onCompleted={(stored) => setCompletedRequest(stored || { status: 'pending' })}
            />
        </WaitlistLayout>
    );
}
